import { emptySquadDirectory } from './group.js';
import { applySquadOp, diffSquads, isSquadOp, revertSquadChange } from './squads.js';
import { buildOrgModel, canReparent, countsFor } from './tree.js';
import { isSquadChange } from './types.js';
import { formatMoney } from './money.js';
import { nowIso, OpError, randomId, splitChangeId } from './util.js';
export { OpError };
export function createScenario(input) {
    const timestamp = nowIso();
    return {
        id: input.id ?? `scn-${randomId()}`,
        name: input.name.trim() || 'Untitled scenario',
        description: input.description?.trim() ?? '',
        createdAt: timestamp,
        updatedAt: timestamp,
        revision: 1,
        baseSyncedAt: input.baseSyncedAt ?? null,
        newPeople: [],
        edits: {},
        departures: [],
        newSquads: [],
        squadEdits: {},
        disbandedSquads: [],
    };
}
/** Empty scenario used when nothing is loaded, so the UI never has to null-check. */
export function emptyScenario(baseSyncedAt = null) {
    return createScenario({ id: 'scn-draft', name: 'Draft', baseSyncedAt });
}
function touch(scenario) {
    return { ...scenario, updatedAt: nowIso() };
}
/** The unedited record a person started from — snapshot row, or the planned-hire definition. */
function baseRecord(snapshot, scenario, personId) {
    const fromSnapshot = snapshot.people.find((p) => p.id === personId);
    if (fromSnapshot)
        return fromSnapshot;
    return scenario.newPeople.find((p) => p.id === personId);
}
/**
 * Whether an edit is really a change.
 *
 * Reference equality is enough for the strings and the nulls, and not for a
 * money value: two objects that say the same thing are different objects, so
 * without this, typing a budget back to the figure the role was created with
 * would leave an override behind that says nothing. Every field this compares
 * is a primitive or `{ amount, currency }`, so a shallow look is the whole job.
 */
function sameValue(a, b) {
    if (a === b)
        return true;
    if ((a ?? null) === null && (b ?? null) === null)
        return true;
    if (typeof a === 'object' && a !== null && typeof b === 'object' && b !== null) {
        const left = a;
        const right = b;
        const keys = Object.keys(left);
        if (keys.length !== Object.keys(right).length)
            return false;
        return keys.every((key) => left[key] === right[key]);
    }
    return false;
}
/**
 * Set one field of one person. If the new value matches the person's base
 * value the override is deleted instead of stored — that is what makes
 * "drag someone back to their old manager" cleanly cancel the change.
 */
function setField(snapshot, scenario, personId, field, value) {
    const base = baseRecord(snapshot, scenario, personId);
    if (!base)
        throw new OpError(`No person with id "${personId}".`);
    const edits = { ...scenario.edits };
    const current = { ...(edits[personId] ?? {}) };
    if (sameValue(value, base[field]))
        delete current[field];
    else
        current[field] = value;
    if (Object.keys(current).length === 0)
        delete edits[personId];
    else
        edits[personId] = current;
    return touch({ ...scenario, edits });
}
/** Narrow a scenario operation to the subset the team layer handles. */
function isSquadOperation(operation) {
    return isSquadOp(operation.op);
}
/**
 * Fold a team operation's result back onto the scenario.
 *
 * The operation itself lives in `squads.ts` because the base overrides run the
 * same ones; all that is left here is stamping `updatedAt`.
 */
function withSquadLayer(scenario, layer) {
    return touch({
        ...scenario,
        newSquads: layer.newSquads,
        squadEdits: layer.squadEdits,
        disbandedSquads: layer.disbandedSquads,
    });
}
// ---------------------------------------------------------------------------
// Operations
// ---------------------------------------------------------------------------
/**
 * Trim the answer and keep only the field its kind uses.
 *
 * Switching the toggle in the dialog leaves the other field's text behind in
 * local state, and without this it would be recorded — so a backfill would ship
 * a justification nobody ever confirmed, and the union's promise that exactly
 * one applies would be a lie.
 */
function normaliseReason(reason) {
    return reason.kind === 'backfill'
        ? { kind: 'backfill', backfillFor: reason.backfillFor.trim() }
        : { kind: 'net-new', justification: reason.justification.trim() };
}
/** One line for a log message or a toast. */
export function describeReason(reason) {
    return reason.kind === 'backfill'
        ? `backfill for ${reason.backfillFor || 'someone unnamed'}`
        : `net new${reason.justification ? ` — ${reason.justification}` : ''}`;
}
/**
 * An id for a role that does not exist yet.
 *
 * Public so a caller can decide the id *before* sending the operation. The UI
 * needs that: it applies the change optimistically and has to know which card
 * to reveal, and letting the server invent a different id means the optimistic
 * one is replaced by a stranger a moment later.
 */
export function plannedHireId() {
    return `new-${randomId()}`;
}
/**
 * The single entry point for mutating a scenario. The web app, the HTTP API and
 * the MCP server all funnel through here, so validation and the resulting
 * change list are identical no matter who is driving.
 */
export function applyOperation(snapshot, scenario, operation, options = {}) {
    const model = buildOrgModel(snapshot, scenario);
    const directory = options.squads ?? emptySquadDirectory();
    /*
     * Team operations are shared with the base overrides, so they are applied by
     * `squads.ts` and only folded back onto the scenario here. The one exception
     * is colour: it belongs to the team wherever the team is looked at — usually
     * because it matches a Jira board — so a plan has no business holding one.
     */
    if (isSquadOperation(operation)) {
        if (operation.op === 'setSquadColor') {
            throw new OpError('A team’s colour belongs to the team rather than to a plan — set it on the base teams.');
        }
        const result = applySquadOp(model, directory, scenario, operation, { proposing: true });
        return {
            scenario: withSquadLayer(scenario, result.layer),
            message: result.message,
            ...(result.personId === undefined ? {} : { personId: result.personId }),
            ...(result.squadId === undefined ? {} : { squadId: result.squadId }),
        };
    }
    switch (operation.op) {
        case 'move': {
            const check = canReparent(model, operation.personId, operation.managerId);
            if (!check.ok)
                throw new OpError(check.reason ?? 'That move is not allowed.');
            const person = model.byId.get(operation.personId);
            const manager = model.byId.get(operation.managerId);
            const moved = countsFor(model, operation.personId).totalReports;
            return {
                scenario: setField(snapshot, scenario, operation.personId, 'managerId', operation.managerId),
                personId: operation.personId,
                message: `${person.name} now reports to ${manager.name}` +
                    (moved > 0 ? ` (bringing ${moved} ${moved === 1 ? 'person' : 'people'} along).` : '.'),
            };
        }
        case 'retitle': {
            const person = model.byId.get(operation.personId);
            if (!person)
                throw new OpError(`No person with id "${operation.personId}".`);
            const title = operation.title.trim();
            if (title.length === 0)
                throw new OpError('A title cannot be empty.');
            return {
                scenario: setField(snapshot, scenario, operation.personId, 'title', title),
                personId: operation.personId,
                message: `${person.name}: "${person.title}" → "${title}".`,
            };
        }
        case 'setDepartment': {
            const person = model.byId.get(operation.personId);
            if (!person)
                throw new OpError(`No person with id "${operation.personId}".`);
            const department = operation.department?.trim() || null;
            return {
                scenario: setField(snapshot, scenario, operation.personId, 'department', department),
                personId: operation.personId,
                message: `${person.name} moved to ${department ?? 'no department'}.`,
            };
        }
        case 'setDottedLine': {
            const person = model.byId.get(operation.personId);
            if (!person)
                throw new OpError(`No person with id "${operation.personId}".`);
            const manager = model.byId.get(operation.managerId);
            if (!manager)
                throw new OpError(`No person with id "${operation.managerId}".`);
            if (person.id === manager.id) {
                throw new OpError(`${person.name} cannot report to themselves.`);
            }
            const current = person.dottedManagerIds ?? [];
            const next = operation.dotted
                ? [...new Set([...current, manager.id])]
                : current.filter((id) => id !== manager.id);
            /*
             * Empty means gone, not an empty list. An override holding `[]` is a
             * change that says nothing — it would sit in the panel forever, because
             * it does not match the base and cannot be typed back to it.
             */
            const scenarioNext = next.length === 0
                ? clearEditField(scenario, operation.personId, 'dottedManagerIds')
                : setField(snapshot, scenario, operation.personId, 'dottedManagerIds', next);
            return {
                scenario: scenarioNext,
                personId: operation.personId,
                message: operation.dotted
                    ? `${person.name} now also answers to ${manager.name}.`
                    : `${person.name} no longer answers to ${manager.name}.`,
            };
        }
        case 'setNotes': {
            const person = model.byId.get(operation.personId);
            if (!person)
                throw new OpError(`No person with id "${operation.personId}".`);
            const notes = operation.notes?.trim() || null;
            return {
                scenario: setField(snapshot, scenario, operation.personId, 'notes', notes),
                personId: operation.personId,
                message: notes ? `Noted against ${person.name}.` : `Note on ${person.name} removed.`,
            };
        }
        case 'setLocation': {
            const person = model.byId.get(operation.personId);
            if (!person)
                throw new OpError(`No person with id "${operation.personId}".`);
            const location = operation.location?.trim() || null;
            return {
                scenario: setField(snapshot, scenario, operation.personId, 'location', location),
                personId: operation.personId,
                message: `${person.name} moved to ${location ?? 'no location'}.`,
            };
        }
        case 'rename': {
            const person = model.byId.get(operation.personId);
            if (!person)
                throw new OpError(`No person with id "${operation.personId}".`);
            const name = operation.name.trim();
            if (name.length === 0)
                throw new OpError('A name cannot be empty.');
            return {
                scenario: setField(snapshot, scenario, operation.personId, 'name', name),
                personId: operation.personId,
                message: `Renamed ${person.name} to ${name}.`,
            };
        }
        case 'setStartDate': {
            const person = model.byId.get(operation.personId);
            if (!person)
                throw new OpError(`No person with id "${operation.personId}".`);
            const startDate = operation.startDate?.trim() || null;
            if (startDate !== null && !/^\d{4}-\d{2}-\d{2}$/.test(startDate)) {
                throw new OpError('Start date must be in YYYY-MM-DD format.');
            }
            return {
                scenario: setField(snapshot, scenario, operation.personId, 'startDate', startDate),
                personId: operation.personId,
                message: `${person.name} start date set to ${startDate ?? 'unset'}.`,
            };
        }
        /*
         * What a planned role is budgeted at.
         *
         * Refused for a real employee, and not because it would be hard: this app
         * does not read pay from the HRIS and is shared with managers to plan with,
         * so the only salary figure it should ever hold is one somebody typed in
         * about a role that does not exist yet.
         */
        case 'setHireCost': {
            const person = model.byId.get(operation.personId);
            if (!person)
                throw new OpError(`No person with id "${operation.personId}".`);
            if (!person.isNew) {
                throw new OpError(`${person.name} is a real employee, so their pay is not this app\u2019s to record.`);
            }
            const cost = operation.cost;
            if (cost && (!Number.isFinite(cost.amount) || cost.amount <= 0)) {
                throw new OpError('A budgeted salary has to be a positive number.');
            }
            const normalised = cost ? { amount: cost.amount, currency: cost.currency.toUpperCase() } : null;
            return {
                scenario: setField(snapshot, scenario, operation.personId, 'plannedCost', normalised),
                personId: operation.personId,
                message: normalised
                    ? `${person.name}: budgeted at ${formatMoney(normalised)}.`
                    : `${person.name}: budget removed.`,
            };
        }
        case 'setHireReason': {
            const person = model.byId.get(operation.personId);
            if (!person)
                throw new OpError(`No person with id "${operation.personId}".`);
            /*
             * The same rule as the budget, for the same reason: a real employee is
             * already employed, so there is nothing to justify and nowhere in the
             * snapshot for an answer to come from.
             */
            if (!person.isNew) {
                throw new OpError(`${person.name} already exists, so there is nothing to justify.`);
            }
            const reason = operation.reason ? normaliseReason(operation.reason) : null;
            return {
                scenario: setField(snapshot, scenario, operation.personId, 'plannedReason', reason),
                personId: operation.personId,
                message: reason ? `${person.name}: ${describeReason(reason)}.` : `${person.name}: reason removed.`,
            };
        }
        case 'addHire': {
            const input = operation.hire;
            const manager = model.byId.get(input.managerId);
            if (!manager)
                throw new OpError(`No manager with id "${input.managerId}".`);
            const title = input.title.trim();
            if (title.length === 0)
                throw new OpError('A planned hire needs a title.');
            const id = input.id ?? plannedHireId();
            if (model.byId.has(id))
                throw new OpError(`Id "${id}" is already in use.`);
            const name = (input.name ?? '').trim() || `Open req — ${title}`;
            const hire = {
                id,
                employeeNumber: null,
                name,
                firstName: null,
                lastName: null,
                email: null,
                title,
                department: input.department?.trim() || manager.department,
                teams: input.teams ?? [],
                location: input.location?.trim() || null,
                country: input.country?.trim() || null,
                managerId: input.managerId,
                startDate: input.startDate?.trim() || null,
                status: 'Planned',
                isNew: true,
                ...(input.cost ? { plannedCost: input.cost } : {}),
                ...(input.reason ? { plannedReason: normaliseReason(input.reason) } : {}),
            };
            return {
                scenario: touch({ ...scenario, newPeople: [...scenario.newPeople, hire] }),
                personId: id,
                message: `Added planned hire "${name}" (${title}) under ${manager.name}.`,
            };
        }
        case 'removeHire': {
            const person = model.byId.get(operation.personId);
            if (!person)
                throw new OpError(`No person with id "${operation.personId}".`);
            if (!person.isNew) {
                throw new OpError(`${person.name} is a real employee, so they cannot be deleted. Use a departure instead if they are leaving.`);
            }
            const directs = countsFor(model, operation.personId).directReports;
            if (directs > 0) {
                throw new OpError(`${person.name} still has ${directs} ${directs === 1 ? 'report' : 'reports'}. Move them elsewhere first.`);
            }
            const edits = { ...scenario.edits };
            delete edits[operation.personId];
            return {
                scenario: touch({
                    ...scenario,
                    newPeople: scenario.newPeople.filter((p) => p.id !== operation.personId),
                    edits,
                    departures: scenario.departures.filter((id) => id !== operation.personId),
                }),
                message: `Removed planned hire "${person.name}".`,
            };
        }
        case 'setDeparture': {
            const person = model.byId.get(operation.personId);
            if (!person)
                throw new OpError(`No person with id "${operation.personId}".`);
            if (person.isNew) {
                throw new OpError(`${person.name} is a planned hire — delete the role instead of marking a departure.`);
            }
            const departures = new Set(scenario.departures);
            if (operation.departing)
                departures.add(operation.personId);
            else
                departures.delete(operation.personId);
            return {
                scenario: touch({ ...scenario, departures: [...departures] }),
                personId: operation.personId,
                message: operation.departing
                    ? `${person.name} flagged as leaving.`
                    : `${person.name} no longer flagged as leaving.`,
            };
        }
        case 'revert': {
            if (operation.changeId.startsWith('squad-')) {
                const result = revertSquadChange(model, directory, scenario, operation.changeId, {
                    proposing: true,
                });
                return {
                    scenario: withSquadLayer(scenario, result.layer),
                    message: result.message,
                    ...(result.personId === undefined ? {} : { personId: result.personId }),
                    ...(result.squadId === undefined ? {} : { squadId: result.squadId }),
                };
            }
            const [kind, personId] = splitChangeId(operation.changeId);
            const person = model.byId.get(personId);
            if (!person)
                throw new OpError(`No person with id "${personId}".`);
            /*
             * A string parsed out of an id, so this switch is not exhaustiveness-checked
             * and a new change kind silently lands in `default` — which reads to the
             * user as "Undo did nothing". Add the arm when you add the kind.
             */
            switch (kind) {
                case 'move':
                    return {
                        scenario: clearEditField(scenario, personId, 'managerId'),
                        personId,
                        message: `Reverted ${person.name}'s reporting line.`,
                    };
                case 'retitle':
                    return {
                        scenario: clearEditField(scenario, personId, 'title'),
                        personId,
                        message: `Reverted ${person.name}'s title.`,
                    };
                case 'department':
                    return {
                        scenario: clearEditField(scenario, personId, 'department'),
                        personId,
                        message: `Reverted ${person.name}'s department.`,
                    };
                case 'location':
                    return {
                        scenario: clearEditField(scenario, personId, 'location'),
                        personId,
                        message: `Reverted ${person.name}'s location.`,
                    };
                case 'dotted':
                    return {
                        scenario: clearEditField(scenario, personId, 'dottedManagerIds'),
                        personId,
                        message: `Reverted ${person.name}'s dotted lines.`,
                    };
                case 'departure':
                    return applyOperation(snapshot, scenario, { op: 'setDeparture', personId, departing: false });
                case 'hire':
                    return applyOperation(snapshot, scenario, { op: 'removeHire', personId });
                default:
                    throw new OpError(`Unknown change id "${operation.changeId}".`);
            }
        }
        case 'clear':
            return {
                scenario: touch({
                    ...scenario,
                    edits: {},
                    newPeople: [],
                    departures: [],
                    newSquads: [],
                    squadEdits: {},
                    disbandedSquads: [],
                }),
                message: 'Cleared every proposed change.',
            };
        default: {
            const exhaustive = operation;
            throw new OpError(`Unsupported operation: ${JSON.stringify(exhaustive)}`);
        }
    }
}
function clearEditField(scenario, personId, field) {
    const edits = { ...scenario.edits };
    const current = { ...(edits[personId] ?? {}) };
    delete current[field];
    if (Object.keys(current).length === 0)
        delete edits[personId];
    else
        edits[personId] = current;
    return touch({ ...scenario, edits });
}
// ---------------------------------------------------------------------------
// Diff
// ---------------------------------------------------------------------------
const KIND_ORDER = {
    move: 0,
    hire: 1,
    retitle: 2,
    department: 3,
    location: 4,
    dotted: 5,
    departure: 6,
    'squad-add': 7,
    'squad-member': 8,
    'squad-rename': 9,
    'squad-parent': 10,
    'squad-disband': 11,
};
/** Team changes sort by team, then by the same order they were applied in. */
function labelOf(change) {
    return isSquadChange(change)
        ? `${change.squadName}${change.kind === 'squad-member' ? ` ${change.personName}` : ''}`
        : change.personName;
}
/**
 * Every difference between the effective org and the base snapshot, plus every
 * team change the scenario proposes.
 *
 * The model was built from the scenario and carries both the effective people
 * and the base ones, so `scenario` is only read for the team layer — the squad
 * directory is not part of the model, because it is shared by every scenario.
 */
export function diffScenario(model, scenario, squads = emptySquadDirectory()) {
    const changes = [...diffSquads(model, scenario, squads)];
    const nameOf = (id) => id ? (model.byId.get(id)?.name ?? null) : null;
    for (const person of model.people) {
        const base = model.baseById.get(person.id);
        if (!base) {
            changes.push({
                id: `hire:${person.id}`,
                kind: 'hire',
                personId: person.id,
                personName: person.name,
                personTitle: person.title,
                isNewPerson: true,
                managerId: person.managerId,
                managerName: nameOf(person.managerId),
                department: person.department,
                startDate: person.startDate,
                cost: person.plannedCost ?? null,
                reason: person.plannedReason ?? null,
            });
            continue;
        }
        if (person.managerId !== base.managerId) {
            changes.push({
                id: `move:${person.id}`,
                kind: 'move',
                personId: person.id,
                personName: person.name,
                personTitle: person.title,
                isNewPerson: false,
                fromManagerId: base.managerId,
                fromManagerName: nameOf(base.managerId),
                toManagerId: person.managerId,
                toManagerName: nameOf(person.managerId),
                movedWith: countsFor(model, person.id).totalReports,
            });
        }
        if (person.title !== base.title) {
            changes.push({
                id: `retitle:${person.id}`,
                kind: 'retitle',
                personId: person.id,
                personName: person.name,
                personTitle: person.title,
                isNewPerson: false,
                fromTitle: base.title,
                toTitle: person.title,
            });
        }
        /*
         * Compared as a set, because the order the lines were added in is not part
         * of what a dotted line means — and a change entry that appeared because two
         * ids swapped places would be a change that says nothing.
         */
        const dotted = [...(person.dottedManagerIds ?? [])].sort();
        const baseDotted = [...(base.dottedManagerIds ?? [])].sort();
        if (dotted.join('') !== baseDotted.join('')) {
            changes.push({
                id: `dotted:${person.id}`,
                kind: 'dotted',
                personId: person.id,
                personName: person.name,
                personTitle: person.title,
                isNewPerson: false,
                toIds: dotted,
                toNames: dotted.map((id) => nameOf(id) ?? id),
            });
        }
        if ((person.location ?? null) !== (base.location ?? null)) {
            changes.push({
                id: `location:${person.id}`,
                kind: 'location',
                personId: person.id,
                personName: person.name,
                personTitle: person.title,
                isNewPerson: false,
                fromLocation: base.location,
                toLocation: person.location,
            });
        }
        if ((person.department ?? null) !== (base.department ?? null)) {
            changes.push({
                id: `department:${person.id}`,
                kind: 'department',
                personId: person.id,
                personName: person.name,
                personTitle: person.title,
                isNewPerson: false,
                fromDepartment: base.department,
                toDepartment: person.department,
            });
        }
        if (model.departures.has(person.id)) {
            changes.push({
                id: `departure:${person.id}`,
                kind: 'departure',
                personId: person.id,
                personName: person.name,
                personTitle: person.title,
                isNewPerson: false,
                managerId: person.managerId,
                managerName: nameOf(person.managerId),
                directReports: countsFor(model, person.id).directReports,
            });
        }
    }
    return changes.sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || labelOf(a).localeCompare(labelOf(b)));
}
export function summarizeChanges(changes) {
    const summary = {
        total: changes.length,
        moves: 0,
        retitles: 0,
        departmentChanges: 0,
        locationChanges: 0,
        dottedChanges: 0,
        hires: 0,
        departures: 0,
        teamChanges: 0,
        headcountDelta: 0,
    };
    for (const change of changes) {
        if (change.kind === 'move')
            summary.moves += 1;
        else if (change.kind === 'retitle')
            summary.retitles += 1;
        else if (change.kind === 'department')
            summary.departmentChanges += 1;
        else if (change.kind === 'location')
            summary.locationChanges += 1;
        else if (change.kind === 'dotted')
            summary.dottedChanges += 1;
        else if (change.kind === 'hire')
            summary.hires += 1;
        else if (change.kind === 'departure')
            summary.departures += 1;
        else if (isSquadChange(change))
            summary.teamChanges += 1;
    }
    summary.headcountDelta = summary.hires - summary.departures;
    return summary;
}
/** Ids of everyone whose reporting line changed — the UI outlines these in red. */
export function movedPersonIds(changes) {
    return new Set(changes.filter((c) => c.kind === 'move').map((c) => c.personId));
}
export function summarizeScenario(model, scenario, squads) {
    const summary = summarizeChanges(diffScenario(model, scenario, squads));
    return {
        id: scenario.id,
        name: scenario.name,
        description: scenario.description,
        createdAt: scenario.createdAt,
        updatedAt: scenario.updatedAt,
        revision: scenario.revision,
        changeCount: summary.total,
        moveCount: summary.moves,
        hireCount: summary.hires,
        titleCount: summary.retitles,
        departureCount: summary.departures,
        teamCount: summary.teamChanges,
    };
}
// ---------------------------------------------------------------------------
// Import / export
// ---------------------------------------------------------------------------
/**
 * The suffix a scenario file carries, wherever it is kept.
 *
 * Here rather than in the server because three places have to agree on it: the
 * server names the files, the browser filters the Drive picker by them, and a
 * Drive listing is only recognisable to a person because of it.
 */
export const SCENARIO_SUFFIX = '.tive-scenario.json';
/**
 * What a scenario is called as a file: `Q4_reorg.scn-fd814e86.tive-scenario.json`.
 *
 * The name is a prefix, and the id is still in there, because both are needed
 * for different reasons. A Drive folder of `scn-fd814e86.tive-scenario.json` is a
 * folder of files nobody can tell apart — and a file somebody cannot identify is
 * a file they eventually delete. The id stays because two plans can be called
 * the same thing, and because it is what makes the name stable when they are.
 *
 * Nothing *reads* the id back out of this. A scenario is identified by what is
 * inside the file, which is what lets the name be re-derived on every save: a
 * rename in the app renames the file the next time it is written, and a file
 * written before this existed is migrated the same way, by being saved.
 */
export function scenarioFileName(scenario) {
    const prefix = fileSafeName(scenario.name);
    return `${prefix ? `${prefix}.` : ''}${scenario.id}${SCENARIO_SUFFIX}`;
}
/**
 * A name that survives being a file name, or an empty string if nothing does.
 *
 * Deliberately conservative about punctuation and not about language: `/` and
 * `:` have to go because Drive and every operating system disagree about them,
 * and a dot has to go because the shape above uses dots to separate its parts.
 * Accented and non-Latin letters stay — stripping them would quietly rename
 * somebody's plan into nothing, which is worse than a long file name.
 */
export function fileSafeName(name) {
    return (name
        .normalize('NFC')
        // Anything that is a separator in some file system, plus the dot this
        // format needs for itself.
        .replaceAll(/[\\/:*?"<>|.]+/gu, ' ')
        // Control characters, which are legal in Drive and legal nowhere else.
        .replaceAll(/\p{C}+/gu, ' ')
        .trim()
        .replaceAll(/\s+/gu, '_')
        // A leading dash reads as a flag to half the command line tools anybody
        // would open this with.
        .replaceAll(/^[-_]+|[-_]+$/gu, '')
        .slice(0, 60)
        .replaceAll(/[-_]+$/gu, ''));
}
/**
 * A MIME type of this app's own, so Drive can tell a scenario from any other
 * JSON somebody has.
 *
 * It is what the Drive file handler is registered against and what the file
 * picker filters on. Matching on the extension instead would catch every
 * `.json` file in the company, because Drive matches the *last* extension and a
 * scenario ends in `.json` like everything else.
 */
export const SCENARIO_MIME = 'application/vnd.tive.scenario+json';
export function toScenarioFile(scenario, snapshot, squads = null) {
    return {
        formatVersion: 1,
        app: 'tive-org-planning',
        exportedAt: nowIso(),
        base: {
            syncedAt: scenario.baseSyncedAt ?? snapshot?.syncedAt ?? null,
            ...(snapshot ? { source: snapshot.source } : {}),
            ...(squads
                ? { squads: { updatedAt: squads.updatedAt, names: touchedSquadNames(scenario, squads) } }
                : {}),
        },
        scenario,
    };
}
/**
 * A name for every team this scenario's layer refers to.
 *
 * Only the ones it touches: an export is a plan, not a copy of the directory,
 * and a team the plan never mentions is one more thing to go stale.
 */
function touchedSquadNames(scenario, squads) {
    const names = {};
    const byId = new Map(squads.squads.map((squad) => [squad.id, squad.name]));
    for (const id of [...Object.keys(scenario.squadEdits), ...scenario.disbandedSquads]) {
        const name = byId.get(id);
        if (name !== undefined)
            names[id] = name;
    }
    // A team the plan invented names itself, and a parent it points at may only
    // exist in the base — both are worth resolving while we still can.
    for (const squad of scenario.newSquads) {
        if (squad.parentId) {
            const parent = byId.get(squad.parentId);
            if (parent !== undefined)
                names[squad.parentId] = parent;
        }
    }
    for (const edit of Object.values(scenario.squadEdits)) {
        if (!edit.parentId)
            continue;
        const parent = byId.get(edit.parentId);
        if (parent !== undefined)
            names[edit.parentId] = parent;
    }
    return names;
}
/**
 * Read an export file whole: the scenario, plus the provenance around it.
 *
 * `parseScenarioFile` is this without the provenance, and is what most callers
 * want. The import path uses this one so it can name the teams a plan was built
 * on even when they have since been renamed or removed.
 */
export function readScenarioFile(input) {
    const scenario = parseScenarioFile(input);
    const record = (typeof input === 'object' && input !== null ? input : {});
    const rawBase = (typeof record.base === 'object' && record.base !== null ? record.base : {});
    const base = {
        syncedAt: typeof rawBase.syncedAt === 'string' ? rawBase.syncedAt : scenario.baseSyncedAt,
    };
    if (typeof rawBase.source === 'string')
        base.source = rawBase.source;
    const rawSquads = (typeof rawBase.squads === 'object' && rawBase.squads !== null ? rawBase.squads : null);
    if (rawSquads) {
        const names = {};
        for (const [id, name] of Object.entries((rawSquads.names ?? {}))) {
            if (typeof name === 'string')
                names[id] = name;
        }
        base.squads = { updatedAt: typeof rawSquads.updatedAt === 'string' ? rawSquads.updatedAt : '', names };
    }
    return { scenario, base };
}
/**
 * Accepts either a wrapped export file or a bare scenario object, and repairs
 * anything missing so an old or hand-edited file still opens.
 */
export function parseScenarioFile(input) {
    if (typeof input !== 'object' || input === null)
        throw new OpError('That file is not valid scenario JSON.');
    const record = input;
    const raw = (typeof record.scenario === 'object' && record.scenario !== null ? record.scenario : record);
    if (typeof raw.name !== 'string' && typeof raw.id !== 'string') {
        throw new OpError('That file does not look like a scenario — expected at least a name or id.');
    }
    const timestamp = nowIso();
    const newPeople = Array.isArray(raw.newPeople) ? raw.newPeople : [];
    const departures = Array.isArray(raw.departures)
        ? raw.departures.filter((d) => typeof d === 'string')
        : [];
    const edits = typeof raw.edits === 'object' && raw.edits !== null ? raw.edits : {};
    // The team layer arrived after the first scenarios were written, so a file
    // without it is normal rather than broken.
    const newSquads = Array.isArray(raw.newSquads) ? raw.newSquads : [];
    const squadEdits = typeof raw.squadEdits === 'object' && raw.squadEdits !== null
        ? raw.squadEdits
        : {};
    const disbandedSquads = Array.isArray(raw.disbandedSquads)
        ? raw.disbandedSquads.filter((id) => typeof id === 'string')
        : [];
    return {
        id: typeof raw.id === 'string' ? raw.id : `scn-${randomId()}`,
        name: typeof raw.name === 'string' && raw.name.trim() ? raw.name.trim() : 'Imported scenario',
        description: typeof raw.description === 'string' ? raw.description : '',
        createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : timestamp,
        // Preserved, not stamped: this is how the app knows which scenario was
        // edited most recently, and reading one off disk is not an edit. Import
        // deliberately re-stamps it, so a file you just opened sorts to the top.
        updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : timestamp,
        revision: typeof raw.revision === 'number' ? raw.revision : 1,
        baseSyncedAt: typeof raw.baseSyncedAt === 'string' ? raw.baseSyncedAt : null,
        newPeople: newPeople.map((person) => ({ ...person, isNew: true, status: 'Planned' })),
        edits,
        departures,
        newSquads: newSquads.map((squad) => ({
            ...squad,
            description: squad.description ?? '',
            members: Array.isArray(squad.members) ? squad.members : [],
            parentId: squad.parentId ?? null,
            color: squad.color ?? null,
        })),
        squadEdits,
        disbandedSquads,
    };
}
/** Human-readable warnings about an imported scenario vs. the current snapshot. */
export function reconcileScenario(snapshot, scenario, squads = emptySquadDirectory(), options = {}) {
    const warnings = [];
    const known = new Set(snapshot.people.map((p) => p.id));
    for (const hire of scenario.newPeople)
        known.add(hire.id);
    /*
     * Naming a team that has been deleted is exactly the case where the directory
     * cannot help, so an imported file's own record of what it was built on is
     * the only source left. Falls back to the id, which is at least a slug of the
     * name it once had.
     */
    const teamName = (squadId) => squads.squads.find((squad) => squad.id === squadId)?.name ??
        scenario.newSquads.find((squad) => squad.id === squadId)?.name ??
        options.teamNames?.[squadId] ??
        squadId;
    const edits = {};
    for (const [personId, edit] of Object.entries(scenario.edits)) {
        if (!known.has(personId)) {
            warnings.push(`Dropped changes for a person who is no longer in the org (${personId}).`);
            continue;
        }
        if (edit.managerId && !known.has(edit.managerId)) {
            warnings.push(`${snapshot.people.find((p) => p.id === personId)?.name ?? personId} was moved under someone who has since left; that move was dropped.`);
            const { managerId: _dropped, ...rest } = edit;
            if (Object.keys(rest).length > 0)
                edits[personId] = rest;
            continue;
        }
        /*
         * A dotted line to somebody who has since left is dropped, not fatal — the
         * same rule the reporting line above follows. The lines that are still
         * meaningful survive; only the dangling one goes.
         */
        if (edit.dottedManagerIds?.some((id) => !known.has(id))) {
            const kept = edit.dottedManagerIds.filter((id) => known.has(id));
            warnings.push(`${snapshot.people.find((p) => p.id === personId)?.name ?? personId} had a dotted line to someone who has since left; it was dropped.`);
            const { dottedManagerIds: _dropped, ...rest } = edit;
            const next = kept.length > 0 ? { ...rest, dottedManagerIds: kept } : rest;
            if (Object.keys(next).length > 0)
                edits[personId] = next;
            continue;
        }
        edits[personId] = edit;
    }
    const departures = scenario.departures.filter((id) => {
        if (known.has(id))
            return true;
        warnings.push(`Dropped a departure for someone no longer in the org (${id}).`);
        return false;
    });
    const newPeople = scenario.newPeople.filter((hire) => {
        if (hire.managerId === null || known.has(hire.managerId))
            return true;
        warnings.push(`Planned hire "${hire.name}" pointed at a manager who is gone; it now needs a new manager.`);
        return true;
    });
    if (scenario.baseSyncedAt && scenario.baseSyncedAt !== snapshot.syncedAt) {
        warnings.push(`This scenario was planned against the org as of ${scenario.baseSyncedAt.slice(0, 10)}; the loaded org is from ${snapshot.syncedAt.slice(0, 10)}.`);
    }
    /*
     * Team changes point at two things that can move underneath them: the shared
     * directory, where somebody may have deleted the team since, and the org,
     * where a member may have left. Both are dropped with a warning rather than
     * left to render as a team that does not exist.
     */
    const knownSquads = new Set([
        ...squads.squads.map((squad) => squad.id),
        ...scenario.newSquads.map((squad) => squad.id),
    ]);
    const squadEdits = {};
    for (const [squadId, edit] of Object.entries(scenario.squadEdits)) {
        if (!knownSquads.has(squadId)) {
            warnings.push(`Dropped the changes to “${teamName(squadId)}” — that team no longer exists.`);
            continue;
        }
        const members = {};
        for (const [personId, role] of Object.entries(edit.members ?? {})) {
            if (known.has(personId))
                members[personId] = role;
            else {
                warnings.push(`Dropped a change to “${teamName(squadId)}” for someone who has left the org (${personId}).`);
            }
        }
        // Every field, not just the ones that existed when this was written: a
        // reconcile that quietly forgets an override is indistinguishable from the
        // user never having made it.
        const next = {};
        if (edit.name !== undefined)
            next.name = edit.name;
        if (edit.description !== undefined)
            next.description = edit.description;
        if (edit.parentId !== undefined && (edit.parentId === null || knownSquads.has(edit.parentId))) {
            next.parentId = edit.parentId;
        }
        else if (edit.parentId) {
            warnings.push(`“${teamName(squadId)}” was inside “${teamName(edit.parentId)}”, which no longer exists, so it is top level now.`);
            next.parentId = null;
        }
        if (edit.color !== undefined)
            next.color = edit.color;
        if (Object.keys(members).length > 0)
            next.members = members;
        if (Object.keys(next).length > 0)
            squadEdits[squadId] = next;
    }
    const disbandedSquads = scenario.disbandedSquads.filter((squadId) => {
        if (knownSquads.has(squadId))
            return true;
        warnings.push(`Dropped the stand-down of “${teamName(squadId)}” — that team no longer exists.`);
        return false;
    });
    const newSquads = scenario.newSquads.map((squad) => ({
        ...squad,
        members: squad.members.filter((member) => {
            if (known.has(member.personId))
                return true;
            warnings.push(`Removed someone from the proposed team “${squad.name}” who has left the org (${member.personId}).`);
            return false;
        }),
    }));
    return {
        scenario: {
            ...scenario,
            edits,
            departures,
            newPeople,
            newSquads,
            squadEdits,
            disbandedSquads,
            baseSyncedAt: scenario.baseSyncedAt ?? snapshot.syncedAt,
        },
        warnings,
    };
}
//# sourceMappingURL=scenario.js.map